import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { AxiosContext, baseAxios, logoutUser } from "./axiosUtil";
import { clearAuth, setTokens } from "../User/userSlice";
import useRefreshToken from "./useRefreshToken";
import useNetworkStatus from "./useNetworkStatus";

let hasShownOfflineMessage = false;

const AxiosProvider = ({ children }) => {
  const dispatch = useDispatch();

  const { user, tokens, unreadCount, expiry } = useSelector(
    (state) => state.user,
  );

  const refreshFn = useRefreshToken();
  const refreshRef = useRef();

  const isOnline = useNetworkStatus();

  // 🔥 keep latest refresh function
  useEffect(() => {
    refreshRef.current = refreshFn;
  }, [refreshFn]);

  // 🔥 FIX: hydration issue (IMPORTANT)
  useEffect(() => {
    if (tokens?.accessToken && !expiry) {
      dispatch(setTokens(tokens));
    }
  }, [tokens, expiry, dispatch]);

  // 🔥 AUTH STATE
  const isAuthenticated = useMemo(() => {
    if (!tokens?.accessToken || !expiry) return false;
    return Date.now() < expiry;
  }, [tokens, expiry]);

  // 🔥 AUTO LOGOUT ON EXPIRY
  useEffect(() => {
    if (!expiry) return;

    const timeLeft = expiry - Date.now();

    if (timeLeft <= 0) {
      dispatch(clearAuth());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(clearAuth());
    }, timeLeft);

    return () => clearTimeout(timer);
  }, [expiry, dispatch]);

  // 🔥 AXIOS INTERCEPTORS
  useEffect(() => {
    const requestInterceptor = baseAxios.interceptors.request.use(
      async (config) => {
        if (!isOnline) {
          if (!hasShownOfflineMessage) {
            console.warn("🚫 No internet");
            hasShownOfflineMessage = true;
          }
          throw new Error("No internet");
        } else {
          hasShownOfflineMessage = false;
        }

        const now = Date.now();

        // 🔥 refresh before expiry
        if (tokens?.accessToken && expiry && expiry - now < 60000) {
          try {
            const newToken = await refreshRef.current();
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (err) {
            dispatch(clearAuth());
            throw err;
          }
        } else if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }

        return config;
      },
      (error) => {
        throw error;
      },
    );

    const responseInterceptor = baseAxios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const prevRequest = error?.config;

        if (!error.response) {
          throw error;
        }

        if (error.response.status === 401 && !prevRequest?._retry) {
          prevRequest._retry = true;

          try {
            const newToken = await refreshRef.current();
            prevRequest.headers.Authorization = `Bearer ${newToken}`;
            return baseAxios(prevRequest);
          } catch (err) {
            dispatch(clearAuth());
            logoutUser(tokens, true);
            throw err;
          }
        }

        throw error;
      },
    );

    return () => {
      baseAxios.interceptors.request.eject(requestInterceptor);
      baseAxios.interceptors.response.eject(responseInterceptor);
    };
  }, [tokens, expiry, isOnline, dispatch]);

  useEffect(() => {
    const syncLogout = (e) => {
      if (e.key === "persist:root" && !e.newValue) {
        dispatch(clearAuth());
      }
    };

    const win = typeof window !== "undefined" ? window : null;

    if (win) {
      win.addEventListener("storage", syncLogout);
    }

    return () => {
      if (win) {
        win.removeEventListener("storage", syncLogout);
      }
    };
  }, [dispatch]);

  // 🔥 CONTEXT VALUE (OPTIMIZED)
  const contextValue = useMemo(
    () => ({
      axios: baseAxios,
      auth: {
        user,
        tokens,
        unreadCount,
        isAuthenticated,
        logout: () => {
          dispatch(clearAuth());
          logoutUser(tokens, true);
        },
      },
    }),
    [user, tokens, unreadCount, isAuthenticated, dispatch],
  );

  return (
    <AxiosContext.Provider value={contextValue}>
      {children}
    </AxiosContext.Provider>
  );
};

AxiosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AxiosProvider;
